/**
 * Anforderungen und Belegstellen als Markierung über gezeichnetem Text (CSS Custom Highlight API) —
 * für die Leseansicht wie für das PDF. Der Text bleibt unberührt; ein Klick auf eine Belegstelle wird
 * über die Caret-Position der Kante zugeordnet.
 */

import { findPhraseRanges } from "./pdfMatch";
import { foldText } from "./search";
import type { RequirementKind } from "./GuidelinesAPI";

export interface TextMark {
  text: string;
  kind: RequirementKind | "link";
  linkId?: string;
}

const HIGHLIGHTS = ["gl-muss", "gl-soll", "gl-kann", "gl-link", "gl-link-current", "gl-search"] as const;

const highlightApi = (): { highlights: Map<string, unknown>; Highlight: new (...ranges: Range[]) => unknown } | null => {
  const css: any = typeof CSS !== "undefined" ? CSS : null;
  const Highlight = (globalThis as any).Highlight;
  return css?.highlights && typeof Highlight === "function" ? { highlights: css.highlights, Highlight } : null;
};

export const clearMarks = () => {
  const api = highlightApi();
  for (const name of HIGHLIGHTS) api?.highlights.delete(name);
};

/** Markiert in den Wurzeln und liefert die Bereiche der Belegstellen für die Klickzuordnung. */
export const paintMarks = (roots: Element[], marks: TextMark[], selectedLink: string | null): Array<{ range: Range; linkId: string }> => {
  const api = highlightApi();
  const byName = new Map<string, Range[]>(HIGHLIGHTS.map((name) => [name, []]));
  const links: Array<{ range: Range; linkId: string }> = [];
  for (const root of roots) {
    findPhraseRanges(root, marks.map((m) => m.text)).forEach((range, i) => {
      if (!range) return;
      const mark = marks[i];
      if (mark.kind === "link" && mark.linkId) {
        links.push({ range, linkId: mark.linkId });
        byName.get(mark.linkId === selectedLink ? "gl-link-current" : "gl-link")!.push(range);
      } else byName.get(`gl-${mark.kind}`)?.push(range);
    });
  }
  if (api)
    for (const [name, ranges] of Array.from(byName.entries())) {
      if (ranges.length) api.highlights.set(name, new api.Highlight(...ranges));
      else api.highlights.delete(name);
    }
  return links;
};

/** Die Belegstelle unter dem Mauszeiger, falls eine. */
export const linkAtPoint = (x: number, y: number, links: Array<{ range: Range; linkId: string }>): string | null => {
  const doc: any = document;
  const caret = doc.caretRangeFromPoint?.(x, y);
  const pos = caret ? { node: caret.startContainer, offset: caret.startOffset } : doc.caretPositionFromPoint?.(x, y);
  const node = pos?.node ?? pos?.offsetNode;
  if (!node) return null;
  const hit = links.find(({ range }) => {
    try {
      return range.isPointInRange(node, pos.offset);
    } catch {
      return false;
    }
  });
  return hit?.linkId ?? null;
};

/**
 * Suchbegriffe markieren — alle Vorkommen, je Textknoten, mit derselben Faltung wie die Suche
 * (Umlaute, Groß/klein). Eigene Markierung `gl-search`, unabhängig von Anforderungen und Belegen.
 */
export const paintSearchTerms = (roots: Element[], terms: string[]): Range | null => {
  const api = highlightApi();
  const ranges: Range[] = [];
  if (terms.length) {
    for (const root of roots) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
        const { folded, origin } = foldText(node.data);
        for (const term of terms) {
          for (let at = folded.indexOf(term); at >= 0; at = folded.indexOf(term, at + term.length)) {
            const range = document.createRange();
            range.setStart(node, origin[at]);
            range.setEnd(node, origin[at + term.length - 1] + 1);
            ranges.push(range);
          }
        }
      }
    }
  }
  if (api) {
    if (ranges.length) api.highlights.set("gl-search", new api.Highlight(...ranges));
    else api.highlights.delete("gl-search");
  }
  return ranges[0] ?? null;
};
